// AddressCleansePage.tsx — /import/addresses
// Pull client contact data from PE (or a file), standardize it against the firm
// naming conventions, review the diff on screen, then download it or push it
// back to PE.
//
// The push reuses the `update-client-record` import use case, so all PE write
// logic stays in api/shared/clientRecordImport.js.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapPin, Upload, Database, Play, Download, CheckCircle2, XCircle, AlertTriangle,
  Loader2, FileSpreadsheet, ShieldCheck, ShieldOff, ArrowRight, Send, X, Filter,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { parseTabularFile, downloadCsv } from '../../utils/parseTabular';
import { runImportQuery, executeImport, type ExecuteRowResult } from '../../services/import';
import {
  cleanseAddresses, uspsStatusLabel, CLEANSE_FIELDS,
  type CleansedRow, type CleanseSummary,
} from '../../services/addressCleanse';

type Source = 'sql' | 'file';
type RowFilter = 'all' | 'changed' | 'warnings' | 'usps';

// ── Field mapping definitions ───────────────────────────────────────────────

const KEY_FIELD = { key: 'ClientCode', label: 'Client Code', required: true };
const NAME_FIELD = { key: 'ClientName', label: 'Client Name', required: false };

const MAPPABLE = [KEY_FIELD, NAME_FIELD, ...CLEANSE_FIELDS.map((f) => ({ ...f, required: false }))];

const DEFAULT_SQL = `-- Active clients with a populated street address.
-- Narrow with a WHERE clause before running against the full client list.
select
    e.ClientCode,
    e.ClientName,
    c.ContAddress,
    c.ContTownCity,
    c.ContCounty,
    c.ContPostCode,
    c.ContPhone,
    c.ContMobile,
    c.ContEmail
from tblEngagement e
join tblContacts c on c.ContIndex = e.ContIndex
where e.ClientStatus = 'ACTIVE'
  and len(isnull(c.ContAddress, '')) > 1
order by e.ClientCode`;

// Column headers are the PE field names, so a file built from this template
// (or downloaded from this page) maps itself on upload. The auto-mapper also
// accepts friendly aliases — Street / City / State / Zip / Phone / Email.
const TEMPLATE_CSV = `ClientCode,ClientName,ContAddress,ContTownCity,ContCounty,ContPostCode,ContPhone,ContMobile,ContEmail
06300-000,Sally W. Strachan,2200 EAST BAY DRIVE,DAPHNE,alabama,36526-1234,(251) 234-1814,,SStrachan1948@Hotmail.COM`;

// ── Small presentational helpers ────────────────────────────────────────────

const TONE_COLORS: Record<string, string> = {
  ok:    '#57d38c',
  warn:  '#e0b341',
  bad:   '#f2686b',
  muted: 'var(--muted)',
};

function Chip({ tone, children }: { tone: 'ok' | 'warn' | 'bad' | 'muted'; children: React.ReactNode }) {
  const color = TONE_COLORS[tone];
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase',
      padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
      color, background: `${color === 'var(--muted)' ? 'rgba(255,255,255,.06)' : color + '22'}`,
      border: `1px solid ${color === 'var(--muted)' ? 'var(--border)' : color + '55'}`,
    }}>
      {children}
    </span>
  );
}

function StatTile({ label, value, tone = 'muted' }: { label: string; value: number | string; tone?: string }) {
  return (
    <div style={{
      flex: '1 1 110px', minWidth: 110, padding: '10px 12px',
      background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)', borderRadius: 8,
    }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: TONE_COLORS[tone] || 'var(--text)' }}>{value}</div>
      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Push confirmation modal ─────────────────────────────────────────────────

function PushConfirmModal({
  count, fieldSummary, onConfirm, onCancel,
}: {
  count: number;
  fieldSummary: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div style={{
        background: 'var(--surface, #1e2030)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw',
        boxShadow: '0 8px 40px rgba(0,0,0,.6)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: 'var(--warn)' }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Push to Practice Engine</span>
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onCancel}><X size={14} /></button>
        </div>

        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
          This writes to <strong style={{ color: 'var(--text)' }}>{count} client record{count !== 1 ? 's' : ''}</strong> in
          PE via <code style={{ fontSize: 11 }}>Clients/Save</code>. Only changed fields are sent.
        </div>
        <div style={{
          fontSize: 12, background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
          borderRadius: 6, padding: 10, marginBottom: 20,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--accent)' }}>Fields being updated</div>
          <div className="muted">{fieldSummary || 'none'}</div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} style={{ fontSize: 13 }}>Cancel</button>
          <button
            className="btn"
            onClick={onConfirm}
            disabled={count === 0}
            style={{
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.5)',
              color: 'var(--accent)', opacity: count === 0 ? .45 : 1,
            }}
          >
            <Send size={13} /> Push {count} Record{count !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export default function AddressCleansePage() {
  // Upload is the default: the PE Query path depends on the SQL Logic App
  // (PORTAL_DATA_URL_BASE64), which isn't reachable over the ODBC route.
  const [source, setSource] = useState<Source>('file');

  // Source data
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Mapping: field key → source column
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Cleanse
  const [useUsps, setUseUsps] = useState(true);
  const [cleansing, setCleansing] = useState(false);
  const [results, setResults] = useState<CleansedRow[] | null>(null);
  const [summary, setSummary] = useState<CleanseSummary | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<RowFilter>('changed');

  // Push
  const [pushing, setPushing] = useState(false);
  const [pushResults, setPushResults] = useState<ExecuteRowResult[] | null>(null);
  const [pushMode, setPushMode] = useState<'cleansed' | 'as-is' | null>(null);

  // ── Reset ─────────────────────────────────────────────────────────────────

  const resetData = useCallback(() => {
    setHeaders([]);
    setRows([]);
    setFileName(null);
    setMapping({});
    setResults(null);
    setSummary(null);
    setSelected(new Set());
    setPushResults(null);
  }, []);

  const resetResults = useCallback(() => {
    setResults(null);
    setSummary(null);
    setSelected(new Set());
    setPushResults(null);
  }, []);

  // ── Auto-map columns whose header matches a field key ─────────────────────
  // Case-insensitive, ignoring spaces/underscores. "ContAddress_Original"
  // deliberately does NOT match "ContAddress", so a downloaded review file
  // re-uploads onto the cleansed columns rather than the originals.

  useEffect(() => {
    if (headers.length === 0) return;
    const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, '');
    const byNorm = new Map<string, string>();
    for (const h of headers) {
      const k = norm(h);
      if (!byNorm.has(k)) byNorm.set(k, h);
    }
    // Friendly aliases for hand-built sheets.
    const ALIASES: Record<string, string[]> = {
      ContAddress:  ['street', 'address', 'addressline1', 'streetaddress', 'address1'],
      ContTownCity: ['city', 'town'],
      ContCounty:   ['state', 'st'],
      ContPostCode: ['zip', 'zipcode', 'postalcode', 'postcode'],
      ContPhone:    ['phone', 'phonenumber'],
      ContMobile:   ['mobile', 'cell', 'cellphone'],
      ContEmail:    ['email', 'emailaddress'],
      ClientCode:   ['clientcode', 'code', 'fullcode'],
      ClientName:   ['clientname', 'name'],
    };

    setMapping((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const f of MAPPABLE) {
        if (next[f.key]) continue;
        let hit = byNorm.get(norm(f.key));
        if (!hit) {
          for (const alias of ALIASES[f.key] || []) {
            hit = byNorm.get(alias);
            if (hit) break;
          }
        }
        if (hit) { next[f.key] = hit; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [headers]);

  // ── Load data ─────────────────────────────────────────────────────────────

  const handleRunQuery = useCallback(async () => {
    if (!sql.trim()) return;
    setSqlLoading(true);
    resetData();
    try {
      const result = await runImportQuery(sql);
      setHeaders(result.headers);
      setRows(result.rows);
      toast.info(`Query returned ${result.totalCount} row${result.totalCount !== 1 ? 's' : ''}`);
    } catch (err: any) {
      toast.error(`Query failed: ${err.message}`);
    } finally {
      setSqlLoading(false);
    }
  }, [sql, resetData]);

  const handleFile = useCallback(async (file: File) => {
    try {
      const { headers: h, rows: r } = await parseTabularFile(file);
      resetData();
      setFileName(file.name);
      setHeaders(h);
      setRows(r);
      toast.info(`Loaded ${r.length} rows from ${file.name}`);
    } catch (err: any) {
      toast.error(err.message);
    }
  }, [resetData]);

  // ── Mapping state ─────────────────────────────────────────────────────────

  const mappedCleanseFields = useMemo(
    () => CLEANSE_FIELDS.filter((f) => !!mapping[f.key]),
    [mapping],
  );

  const canCleanse = rows.length > 0 && !!mapping.ClientCode && mappedCleanseFields.length > 0;

  /** Build the payload rows sent to the cleanse endpoint. */
  const buildCleanseRows = useCallback((): Record<string, string>[] => {
    return rows.map((row) => {
      const out: Record<string, string> = {};
      for (const f of MAPPABLE) {
        const col = mapping[f.key];
        if (!col) continue;      // unmapped → omitted, never blanked in PE
        out[f.key] = row[col] ?? '';
      }
      return out;
    });
  }, [rows, mapping]);

  // ── Cleanse ───────────────────────────────────────────────────────────────

  const handleCleanse = useCallback(async () => {
    if (!canCleanse) return;
    setCleansing(true);
    resetResults();
    try {
      const res = await cleanseAddresses(buildCleanseRows(), useUsps);
      setResults(res.results);
      setSummary(res.summary);
      // Pre-select every pushable row — the common case is "apply all of this".
      setSelected(new Set(res.results.filter((r) => r.pushable).map((r) => r.row)));
      setFilter('changed');
      toast.info(`${res.summary.changed} of ${res.summary.total} records need changes`);
      if (useUsps && !res.summary.uspsAvailable) {
        toast.warn('USPS credentials not configured — local rules only');
      }
    } catch (err: any) {
      toast.error(`Cleanse failed: ${err.message}`);
    } finally {
      setCleansing(false);
    }
  }, [canCleanse, buildCleanseRows, useUsps, resetResults]);

  // ── Filtering + selection ─────────────────────────────────────────────────

  const visibleRows = useMemo(() => {
    if (!results) return [];
    switch (filter) {
      case 'changed':  return results.filter((r) => r.changed);
      case 'warnings': return results.filter((r) => r.warnings.length > 0);
      case 'usps':     return results.filter(
        (r) => r.usps && ['undeliverable', 'not_found', 'missing_unit', 'unit_unconfirmed'].includes(r.usps.status),
      );
      default:         return results;
    }
  }, [results, filter]);

  const toggleRow = useCallback((rowNum: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowNum)) next.delete(rowNum);
      else next.add(rowNum);
      return next;
    });
  }, []);

  const selectedPushable = useMemo(
    () => (results || []).filter((r) => r.pushable && selected.has(r.row)),
    [results, selected],
  );

  const pushFieldSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of selectedPushable) {
      for (const key of Object.keys(r.updateRecord)) {
        if (key === 'ClientCode') continue;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k, n]) => `${k} (${n})`)
      .join(', ');
  }, [selectedPushable]);

  // ── Download ──────────────────────────────────────────────────────────────

  const handleDownload = useCallback(() => {
    if (!results) return;

    // Original + cleansed columns side by side. The cleansed columns keep their
    // exact PE field names so this file re-uploads and pushes without remapping.
    const cols = ['Row', 'ClientCode', 'ClientName'];
    for (const f of CLEANSE_FIELDS) {
      if (!mapping[f.key]) continue;
      cols.push(`${f.key}_Original`, f.key);
    }
    cols.push('Changed', 'USPS_Status', 'Warnings');

    const out = results.map((r) => {
      const line: (string | number)[] = [r.row, r.clientCode, r.clientName];
      for (const f of CLEANSE_FIELDS) {
        if (!mapping[f.key]) continue;
        const field = r.fields[f.key];
        line.push(field ? field.before : '', field ? field.after : '');
      }
      line.push(
        r.changed ? 'YES' : '',
        r.usps ? r.usps.status : '',
        r.warnings.map((w) => `${w.label}: ${w.message}`).join(' | '),
      );
      return line;
    });

    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    downloadCsv(`address-cleanse-${ts}.csv`, cols, out);
    toast.info(`Downloaded ${out.length} rows — review, then re-upload to push`);
  }, [results, mapping]);

  // ── Push ──────────────────────────────────────────────────────────────────

  /** Records for the "push reviewed file as-is" path (no cleanse step). */
  const asIsRecords = useMemo(() => {
    if (!mapping.ClientCode) return [];
    return rows
      .map((row) => {
        const rec: Record<string, string> = { ClientCode: (row[mapping.ClientCode] || '').trim() };
        for (const f of CLEANSE_FIELDS) {
          const col = mapping[f.key];
          if (!col) continue;
          const val = (row[col] || '').trim();
          if (val !== '') rec[f.key] = val;
        }
        return rec;
      })
      .filter((r) => r.ClientCode !== '' && Object.keys(r).length > 1);
  }, [rows, mapping]);

  const asIsFieldSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of asIsRecords) {
      for (const key of Object.keys(r)) {
        if (key === 'ClientCode') continue;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k, n]) => `${k} (${n})`).join(', ');
  }, [asIsRecords]);

  const runPush = useCallback(async (records: Record<string, string>[]) => {
    setPushMode(null);
    if (records.length === 0) {
      toast.info('Nothing to push');
      return;
    }
    setPushing(true);
    setPushResults(null);
    try {
      const res = await executeImport('update-client-record', records);
      setPushResults(res.results);
      if (res.failed === 0) toast.info(`${res.succeeded} of ${res.total} clients updated in PE`);
      else toast.error(`${res.failed} of ${res.total} failed`);
    } catch (err: any) {
      toast.error(`Push failed: ${err.message}`);
    } finally {
      setPushing(false);
    }
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  const pendingRecords = pushMode === 'as-is'
    ? asIsRecords
    : selectedPushable.map((r) => r.updateRecord);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <MapPin size={20} style={{ color: 'var(--accent)' }} /> Address Validator
        </h1>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          Standardize client street, city, state, zip, phone and email against the firm naming
          conventions — then download the results for review, or push them straight back to PE.
        </p>
      </div>

      {/* Source */}
      <div className="card">
        <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {source === 'sql' ? <Database size={16} /> : <FileSpreadsheet size={16} />}
          <span style={{ fontWeight: 600, flex: 1 }}>Source</span>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {([
              { v: 'file' as Source, label: 'Upload File', icon: <Upload size={12} /> },
              { v: 'sql' as Source,  label: 'PE Query',    icon: <Database size={12} /> },
            ]).map((opt) => (
              <button
                key={opt.v}
                onClick={() => { setSource(opt.v); resetData(); }}
                style={{
                  padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: 'none', display: 'flex', alignItems: 'center', gap: 5,
                  background: source === opt.v ? 'var(--accent)' : 'transparent',
                  color: source === opt.v ? '#0b0d10' : 'var(--muted)',
                }}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="bd">
          {source === 'sql' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                fontSize: 12, padding: 10, borderRadius: 6,
                background: 'rgba(224,179,65,.08)', border: '1px solid rgba(224,179,65,.3)',
              }}>
                <strong>Requires the SQL Logic App.</strong> This path goes through{' '}
                <code>PORTAL_DATA_URL_BASE64</code> and won&apos;t work where PE is reached over
                ODBC — use <strong>Upload File</strong> in that case.
              </div>
              <textarea
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                spellCheck={false}
                style={{
                  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                  borderRadius: 6, padding: 12, fontSize: 12, fontFamily: 'monospace',
                  minHeight: 200, resize: 'vertical', width: '100%',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="btn"
                  onClick={handleRunQuery}
                  disabled={sqlLoading || !sql.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  {sqlLoading ? <Loader2 size={13} className="spin" /> : <Play size={13} />} Run Query
                </button>
                {rows.length > 0 && (
                  <span className="muted" style={{ fontSize: 12 }}>{rows.length} rows loaded</span>
                )}
              </div>
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, marginBottom: 10, flexWrap: 'wrap',
              }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  Export client contact data from PE, then upload it here.
                </span>
                <button
                  className="btn"
                  onClick={() => {
                    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'address-validator-template.csv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                >
                  <FileSpreadsheet size={13} /> Download template
                </button>
              </div>
              <div
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: '2px dashed var(--border)', borderRadius: 8, padding: '32px 20px',
                  textAlign: 'center', cursor: 'pointer',
                }}
              >
                <Upload size={28} style={{ color: 'var(--muted)', marginBottom: 8 }} />
                <div style={{ fontSize: 14 }}>
                  {fileName
                    ? <><strong>{fileName}</strong> — {rows.length} row{rows.length !== 1 ? 's' : ''} loaded</>
                    : 'Drop a CSV or Excel file here, or click to browse'}
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                  A file downloaded from this page re-uploads with its columns already mapped
                </div>
                <input
                  ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.tsv,.txt"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                  style={{ display: 'none' }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Field mapping */}
      {headers.length > 0 && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Field Mapping</div>
          <div className="bd">
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 12 }}>
              Client Code is required. Map at least one address, phone or email field — anything left
              unmapped is never touched in PE.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {MAPPABLE.map((f) => (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12, width: 96, flexShrink: 0 }}>
                    {f.label}
                    {f.required && <span style={{ color: 'var(--warn)' }}> *</span>}
                  </label>
                  <select
                    value={mapping[f.key] || ''}
                    onChange={(e) => {
                      setMapping((prev) => ({ ...prev, [f.key]: e.target.value }));
                      resetResults();
                    }}
                    style={{
                      background: 'var(--bg)', color: 'var(--text)', flex: 1, minWidth: 0,
                      border: `1px solid ${f.required && !mapping[f.key] ? 'rgba(242,104,107,.5)' : 'var(--border)'}`,
                      borderRadius: 6, padding: '5px 8px', fontSize: 12,
                    }}
                  >
                    <option value="">— not mapped —</option>
                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cleanse controls */}
      {rows.length > 0 && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Standardize</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useUsps}
                onChange={(e) => setUseUsps(e.target.checked)}
                style={{ marginTop: 3, accentColor: 'var(--accent)' }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {useUsps ? <ShieldCheck size={13} style={{ color: '#57d38c' }} /> : <ShieldOff size={13} />}
                  Verify against USPS
                </div>
                <div className="muted" style={{ fontSize: 11 }}>
                  Catches misspelled streets, wrong ZIPs and undeliverable addresses. Falls back to
                  local rules automatically when USPS credentials aren&apos;t configured.
                  {summary && !summary.uspsAvailable && (
                    <strong style={{ color: 'var(--warn)' }}> Not currently configured.</strong>
                  )}
                </div>
              </div>
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                className="btn"
                onClick={handleCleanse}
                disabled={!canCleanse || cleansing}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
                  background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.5)',
                  color: 'var(--accent)', opacity: !canCleanse || cleansing ? .45 : 1,
                }}
              >
                {cleansing ? <Loader2 size={13} className="spin" /> : <Play size={13} />}
                Standardize {rows.length} Record{rows.length !== 1 ? 's' : ''}
              </button>

              {source === 'file' && asIsRecords.length > 0 && (
                <button
                  className="btn"
                  onClick={() => setPushMode('as-is')}
                  disabled={pushing}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                  title="Push the mapped values straight to PE without standardizing — for a file you already reviewed"
                >
                  <Send size={12} /> Push {asIsRecords.length} as-is to PE
                </button>
              )}

              {!canCleanse && (
                <span className="muted" style={{ fontSize: 12 }}>
                  {!mapping.ClientCode ? 'Map Client Code to continue' : 'Map at least one field to standardize'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Results</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <StatTile label="Records" value={summary.total} />
              <StatTile label="Need changes" value={summary.changed} tone="warn" />
              <StatTile label="Already clean" value={summary.unchanged} tone="ok" />
              <StatTile label="With warnings" value={summary.withWarnings} tone={summary.withWarnings ? 'warn' : 'muted'} />
              {summary.uspsActive && <StatTile label="USPS verified" value={summary.uspsVerified} tone="ok" />}
              {summary.uspsActive && <StatTile label="USPS failed" value={summary.uspsFailed} tone={summary.uspsFailed ? 'bad' : 'muted'} />}
            </div>

            {!summary.uspsAvailable && (
              <div style={{
                fontSize: 12, padding: 10, borderRadius: 6,
                background: 'rgba(224,179,65,.08)', border: '1px solid rgba(224,179,65,.3)',
              }}>
                <strong>Local rules only.</strong> USPS credentials aren&apos;t configured, so misspelled
                streets and wrong ZIPs won&apos;t be caught. Set <code>USPS_CLIENT_ID</code> and{' '}
                <code>USPS_CLIENT_SECRET</code> to enable verification.
              </div>
            )}

            {/* Filter + actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Filter size={13} style={{ color: 'var(--muted)' }} />
              <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                {([
                  { v: 'changed' as RowFilter,  label: `Changed (${summary.changed})` },
                  { v: 'warnings' as RowFilter, label: `Warnings (${summary.withWarnings})` },
                  ...(summary.uspsActive ? [{ v: 'usps' as RowFilter, label: `USPS flags (${summary.uspsFailed})` }] : []),
                  { v: 'all' as RowFilter,      label: `All (${summary.total})` },
                ]).map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setFilter(opt.v)}
                    style={{
                      padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: filter === opt.v ? 'var(--accent)' : 'transparent',
                      color: filter === opt.v ? '#0b0d10' : 'var(--muted)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div style={{ flex: 1 }} />

              <button className="btn" onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <Download size={13} /> Download for review
              </button>
              <button
                className="btn"
                onClick={() => setPushMode('cleansed')}
                disabled={selectedPushable.length === 0 || pushing}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                  background: 'rgba(87,211,140,.15)', borderColor: 'rgba(87,211,140,.5)',
                  color: '#57d38c', opacity: selectedPushable.length === 0 || pushing ? .45 : 1,
                }}
              >
                {pushing ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
                Push {selectedPushable.length} to PE
              </button>
            </div>

            {/* Row list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visibleRows.length === 0 ? (
                <div className="muted" style={{ fontSize: 12, padding: 12, textAlign: 'center' }}>
                  No records match this filter.
                </div>
              ) : (
                visibleRows.map((r) => {
                  const pushResult = pushResults?.find((p) => p.clientCode === r.clientCode);
                  const changedFields = Object.entries(r.fields).filter(([, f]) => f!.changed);
                  const uspsChip = r.usps ? uspsStatusLabel(r.usps.status) : null;

                  return (
                    <div
                      key={r.row}
                      style={{
                        border: '1px solid var(--border)', borderRadius: 8, padding: 12,
                        background: 'rgba(255,255,255,.02)',
                        borderLeft: pushResult
                          ? `3px solid ${pushResult.status === 'success' ? '#57d38c' : '#f2686b'}`
                          : '1px solid var(--border)',
                      }}
                    >
                      {/* Row header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.row)}
                          disabled={!r.pushable}
                          onChange={() => toggleRow(r.row)}
                          title={r.pushable ? 'Include in push' : 'Nothing to push for this row'}
                          style={{ accentColor: 'var(--accent)' }}
                        />
                        <code style={{ fontSize: 12, fontWeight: 700 }}>{r.clientCode || '(no code)'}</code>
                        <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 100 }}>{r.clientName}</span>

                        {uspsChip && <Chip tone={uspsChip.tone}>{uspsChip.label}</Chip>}
                        {r.changed
                          ? <Chip tone="warn">{r.changeCount} change{r.changeCount !== 1 ? 's' : ''}</Chip>
                          : <Chip tone="ok">clean</Chip>}
                        {pushResult && (
                          pushResult.status === 'success'
                            ? <Chip tone="ok">pushed</Chip>
                            : <Chip tone="bad">push failed</Chip>
                        )}
                      </div>

                      {/* Field diffs */}
                      {changedFields.length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {changedFields.map(([key, f]) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
                              <span className="muted" style={{ width: 52, flexShrink: 0, fontSize: 11 }}>{f!.label}</span>
                              <span style={{
                                fontFamily: 'monospace', fontSize: 11, color: '#f2686b',
                                textDecoration: 'line-through', opacity: .8,
                              }}>
                                {f!.before || '(empty)'}
                              </span>
                              <ArrowRight size={11} style={{ color: 'var(--muted)', flexShrink: 0 }} />
                              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#57d38c', fontWeight: 600 }}>
                                {f!.after || '(empty)'}
                              </span>
                              {f!.uspsCorrected && <Chip tone="ok">USPS</Chip>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Warnings */}
                      {r.warnings.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {r.warnings.map((w, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#e0b341' }}>
                              <AlertTriangle size={11} style={{ marginTop: 2, flexShrink: 0 }} />
                              <span><strong>{w.label}:</strong> {w.message}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Push error */}
                      {pushResult?.error && (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#f2686b' }}>
                          <XCircle size={11} style={{ marginTop: 2, flexShrink: 0 }} />
                          <span>{pushResult.error}</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Push summary */}
      {pushResults && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} style={{ color: '#57d38c' }} /> Push Complete
          </div>
          <div className="bd" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <StatTile label="Attempted" value={pushResults.length} />
            <StatTile label="Succeeded" value={pushResults.filter((p) => p.status === 'success').length} tone="ok" />
            <StatTile label="Failed" value={pushResults.filter((p) => p.status === 'error').length} tone="bad" />
          </div>
        </div>
      )}

      {pushMode && (
        <PushConfirmModal
          count={pendingRecords.length}
          fieldSummary={pushMode === 'as-is' ? asIsFieldSummary : pushFieldSummary}
          onConfirm={() => runPush(pendingRecords)}
          onCancel={() => setPushMode(null)}
        />
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
